import { t } from 'i18next'

import { getActionCommandRequest, getAllPagesData } from '@helpers'
import type { ExecutionContext } from '@helpers/getCommandRequestData'
import type {
  ActionRow,
  ExecuteCommandBody,
  ExecuteCommandPayload,
  FormRow,
  MutationType,
  ObjectDataRecord,
  Row,
} from '@types'

import { mockPrintFormsStore } from '../mock/printFormsMock'
import type { PrintFormConfig } from '../types'

/** Временный print-form для action `print_form_vou` (удалить после бэкенд-реализации). */
export const PRINT_FORM_VOU_TEMP_ID = 'voucher-labels'

export type PrintFormVouPayload = {
  config: PrintFormConfig
  records: ObjectDataRecord[]
}

export type RunPrintFormVouFromActionParams = {
  executeCommand: MutationType<ExecuteCommandPayload, unknown>
  action: ActionRow
  currentRowValues: Row
  fieldsFormConfig?: FormRow[]
  scriptContext: Record<string, unknown>
  commandExecutionContext?: ExecutionContext
}

export const runPrintFormVouFromAction = async ({
  executeCommand,
  action,
  currentRowValues,
  fieldsFormConfig,
  scriptContext,
  commandExecutionContext,
}: RunPrintFormVouFromActionParams): Promise<PrintFormVouPayload> => {
  const printConfig = mockPrintFormsStore.getById(PRINT_FORM_VOU_TEMP_ID)
  if (!printConfig) {
    throw new Error(
      t('printForms.printFormVou.errors.configNotFound', { id: PRINT_FORM_VOU_TEMP_ID })
    )
  }

  const request = getActionCommandRequest({
    action,
    currentRowValues,
    scriptContext,
    fieldsFormConfig,
    commandExecutionContext,
  })
  if (!request) {
    throw new Error(t('printForms.printFormVou.errors.commandRequired'))
  }
  if (request.hasFile) {
    throw new Error(t('printForms.printFormVou.errors.noFileBody'))
  }
  if (request.isCommandResponse) {
    throw new Error(t('printForms.printFormVou.errors.needsTabularData'))
  }

  const commandParams = {
    method: request.method,
    objectCode: request.objectCode,
    commandCode: request.commandCode,
    params: request.params,
    body: request.method !== 'GET' ? (request.body as ExecuteCommandBody) : undefined,
    resource: request.resource,
    transformResponseWithPagination: true,
    size: 1000,
  }

  const firstPageData = await executeCommand(commandParams).unwrap()

  const records = await getAllPagesData({
    firstPageData: firstPageData as { data: ObjectDataRecord[]; totalCount: number },
    executeCommand: payload => executeCommand(payload),
    commandParams,
  })

  if (!records.length) {
    throw new Error(t('printForms.printFormVou.errors.noRecords'))
  }

  return {
    config: printConfig,
    records,
  }
}
